import { ChevronRight } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import type { DocumentFolderDto, PagedResult } from "@/types/api";

/**
 * Prompt 6 — DMS folder browser at scale, list surface only. The
 * Documents page top bar now owns the category pills, search box, and
 * sort control (the layout redesign lifted them out of this sidebar); this
 * component renders the paginated root-folder list for the selected
 * category plus its Prev/Next controls. Drill-in is delegated to the
 * parent via onRootSelect.
 */

export const ROOT_BROWSER_PAGE_SIZE = 50;

export interface EntityRootBrowserProps {
  category: string | null;
  q: string;
  hits?: PagedResult<DocumentFolderDto>;
  isError: boolean;
  isFetching: boolean;
  page: number;
  onPageChange: (page: number) => void;
  onRootSelect: (root: DocumentFolderDto) => void;
}

export function EntityRootBrowser({
  category,
  q,
  hits,
  isError,
  isFetching,
  page,
  onPageChange,
  onRootSelect,
}: EntityRootBrowserProps) {
  const totalCount = hits?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / ROOT_BROWSER_PAGE_SIZE));
  const trimmedQ = q.trim();

  return (
    <div>
      {isError && (
        <p className="text-caption text-status-error">
          Couldn't load folders. Try again in a moment.
        </p>
      )}

      {!category && (
        <p className="text-caption text-gray-mid">
          Pick a category to browse its folders.
        </p>
      )}

      {category && hits && hits.data.length === 0 && !isFetching && (
        <p className="text-caption text-gray-mid">
          {trimmedQ
            ? `No ${category.toLowerCase()} folders match "${trimmedQ}".`
            : `No ${category.toLowerCase()} folders yet.`}
        </p>
      )}

      {category && hits && hits.data.length > 0 && (
        <ul className="flex flex-col divide-y divide-border-light border border-border-light rounded-md overflow-hidden">
          {hits.data.map((root) => (
            <li key={root.id}>
              <button
                type="button"
                className="w-full flex items-center justify-between gap-space-2 px-space-3 py-space-2 text-left hover:bg-warm-white"
                onClick={() => onRootSelect(root)}
              >
                <span className="flex flex-col min-w-0">
                  <span className="text-small text-onyx truncate">{root.name}</span>
                  <span className="text-caption text-gray-mid">
                    {root.childFolderCount} folder
                    {root.childFolderCount === 1 ? "" : "s"} ·{" "}
                    {root.documentCount} doc
                    {root.documentCount === 1 ? "" : "s"}
                  </span>
                </span>
                <ChevronRight size={14} aria-hidden className="text-gray-mid shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {category && totalCount > ROOT_BROWSER_PAGE_SIZE && (
        <div className="mt-space-2 flex items-center justify-between text-caption text-gray-mid">
          <span>
            Page {page} of {totalPages} · {totalCount} total
          </span>
          <span className="flex items-center gap-space-1">
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1 || isFetching}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              Prev
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= totalPages || isFetching}
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            >
              Next
            </Button>
          </span>
        </div>
      )}
    </div>
  );
}
